<script lang="ts">
	// Google Consent Mode v2 — load GTM once (per privacy gates), gate tags via consent signals.
	// https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced
	import { onMount } from "svelte";
	import {
		buildConsentUpdate,
		shouldHonorDoNotTrack,
		shouldLoadGtmScript,
	} from "#lib/googleConsent.js";

	const containerId = "GTM-PR49RXC";

	interface Props {
		analyticsAccepted?: boolean;
		marketingAccepted?: boolean;
	}

	let { analyticsAccepted = false, marketingAccepted = false }: Props = $props();

	// Default true so SSR never emits the GTM <script> tag — `navigator` is browser-only,
	// and we don't want a Google request to fire before we know the visitor's DNT/GPC state.
	let dntRespected = $state(true);
	let shouldLoadGtm = $derived(shouldLoadGtmScript(marketingAccepted, dntRespected));

	onMount(() => {
		dntRespected = shouldHonorDoNotTrack(navigator);
	});

	// Fires on mount AND on every prop change. Sending an explicit `update`
	// (granted or denied) is required by Consent Mode v2 — denial enables
	// cookieless pings instead of silently dropping data.
	$effect(() => {
		if (typeof window === "undefined" || typeof window.gtag !== "function") return;
		window.gtag("consent", "update", buildConsentUpdate({ analyticsAccepted, marketingAccepted }));
	});
</script>

<svelte:head>
	<script>
		// Default consent MUST be set before GTM loads.
		window.dataLayer = window.dataLayer || [];
		window.gtag =
			window.gtag ||
			function () {
				window.dataLayer.push(arguments);
			};

		window.gtag("consent", "default", {
			analytics_storage: "denied",
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			functionality_storage: "denied",
			personalization_storage: "denied",
			security_storage: "granted",
			wait_for_update: 500,
		});

		// Strip ad identifiers from outgoing pings when ad_storage is denied.
		window.gtag("set", "ads_data_redaction", true);

		window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
	</script>

	{#if shouldLoadGtm}
		<script async src="https://www.googletagmanager.com/gtm.js?id={containerId}"></script>
	{/if}
</svelte:head>
